import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { SnapmonLogo } from '../components/SnapmonLogo';
import { Pill } from '../components/Pill';
import { Icon } from '../components/Icons';
import { authApi } from '../api/auth';
import { useAuthStore } from '../auth/store';
import type { ApiError } from '../api/client';

const Schema = z.object({
  currentPassword: z.string().min(1, 'Required'),
  newPassword: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string(),
}).refine((d) => d.newPassword === d.confirmPassword, {
  path: ['confirmPassword'],
  message: 'Passwords do not match',
});

type FormValues = z.infer<typeof Schema>;

export function ChangePasswordScreen() {
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const clear = useAuthStore((s) => s.clear);
  const mustChangePassword = useAuthStore((s) => s.mustChangePassword);
  const [formError, setFormError] = useState<string | null>(null);

  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const mutation = useMutation({
    mutationFn: (v: FormValues) => authApi.changePassword(v.currentPassword, v.newPassword),
    onSuccess: (res) => {
      // Refresh token because the server may have rotated claims (must_change_password is now false).
      setSession(res.token, res.creator, false);
      navigate('/', { replace: true });
    },
    onError: (err: ApiError) => {
      setFormError(err?.message ?? 'Password change failed');
    },
  });

  const onSubmit = (vals: FormValues) => {
    const parsed = Schema.safeParse(vals);
    if (!parsed.success) {
      setFormError(parsed.error.errors[0]?.message ?? 'Invalid input');
      return;
    }
    setFormError(null);
    mutation.mutate(parsed.data);
  };

  return (
    <div style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 420, display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <SnapmonLogo />
          <button
            onClick={() => { clear(); navigate('/login', { replace: true }); }}
            style={{ fontSize: 12, color: 'var(--ink-3)' }}
          >
            Sign out
          </button>
        </div>
        <Pill tone="brand" size="md">Secure your account</Pill>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 600, letterSpacing: '-0.02em', margin: 0 }}>
          {mustChangePassword ? 'Choose a new password' : 'Update your password'}
        </h1>
        <p style={{ color: 'var(--ink-3)', fontSize: 13, margin: 0 }}>
          {mustChangePassword
            ? 'Your account was created with a one-time password. Pick something only you know — minimum 8 characters.'
            : 'Enter your current password, then choose a new one.'}
        </p>

        <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <PwField
            label={mustChangePassword ? 'Temporary password' : 'Current password'}
            register={register('currentPassword')}
            error={errors.currentPassword?.message}
            placeholder={mustChangePassword ? 'From admin' : '••••••••'}
          />
          <PwField label="New password" register={register('newPassword')} error={errors.newPassword?.message} />
          <PwField label="Confirm new password" register={register('confirmPassword')} error={errors.confirmPassword?.message} />

          {formError && (
            <div
              style={{
                fontSize: 12.5,
                color: 'var(--bad)',
                background: 'color-mix(in oklab, var(--bad) 10%, var(--bg-elev))',
                border: '1px solid color-mix(in oklab, var(--bad) 25%, transparent)',
                padding: '8px 12px',
                borderRadius: 'var(--r-sm)',
              }}
            >
              {formError}
            </div>
          )}

          <button
            type="submit"
            disabled={mutation.isPending}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              background: 'var(--brand)',
              color: '#fff',
              padding: '12px 16px',
              borderRadius: 'var(--r-md)',
              fontWeight: 600,
              fontSize: 14,
              marginTop: 6,
            }}
          >
            {mutation.isPending ? (
              <><span className="snapmon-spinner" /> Saving…</>
            ) : (
              <>Save new password <Icon.Arrow /></>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

function PwField({ label, register, placeholder = '••••••••', error }: { label: string; register: any; placeholder?: string; error?: string }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 11.5, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>{label}</span>
      <input
        {...register}
        type="password"
        placeholder={placeholder}
        style={{
          background: 'var(--bg-elev)',
          border: '1px solid ' + (error ? 'var(--bad)' : 'var(--line-strong)'),
          borderRadius: 'var(--r-md)',
          padding: '10px 12px',
          fontFamily: 'var(--font-mono)',
          letterSpacing: '0.18em',
          fontSize: 14,
          color: 'var(--ink)',
          outline: 'none',
        }}
      />
      {error && <span style={{ fontSize: 11, color: 'var(--bad)' }}>{error}</span>}
    </label>
  );
}
